import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const PaymentTrackerEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [currentUserRole, setCurrentUserRole] = useState('')
  const [suppliers, setSuppliers] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(true)

  const [formData, setFormData] = useState({
    supplierName: '',
    supplierId: '',
    beneficiaryAccountNumber: '',
    beneficiaryIfscNumber: '',
    amount: '',
    debit: '',
    credit: '',
    requestStatus: 'Advance',
    paymentDate: '',
    paymentStatus: 'Pending',
    invoiceStatus: '',
    comments: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  // Fetch payment data
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (!id) {
        toast.error('Payment ID is missing', { autoClose: 2000 })
        navigate('/paymentTracker')
        return
      }

      setIsLoadingData(true)
      try {
        const payload = {
          action: 'get-payment-by-id',
          id: id
        }

        const response = await axios.post(API_URL, payload)
        let responseData = response.data

        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (parseError) {
            responseData = response.data
          }
        }

        if (responseData.success === true && responseData.data) {
          const payment = responseData.data
          setFormData({
            supplierName: payment.supplierName || '',
            supplierId: payment.supplierId?._id || payment.supplierId || '',
            beneficiaryAccountNumber: payment.beneficiaryAccountNumber || '',
            beneficiaryIfscNumber: payment.beneficiaryIfscNumber || '',
            amount: payment.amount != null ? payment.amount.toString() : '',
            debit: payment.debit || '',
            credit: payment.credit || '',
            requestStatus: payment.requestStatus || 'Advance',
            paymentDate: payment.paymentDate ? new Date(payment.paymentDate).toISOString().slice(0, 10) : '',
            paymentStatus: payment.paymentStatus || 'Pending',
            invoiceStatus: ['Pending', 'Accounted'].includes(payment.invoiceStatus)
              ? payment.invoiceStatus
              : '',
            comments: payment.comments || ''
          })
        } else {
          toast.error(responseData.message || 'Failed to fetch payment details', { autoClose: 2000 })
          navigate('/paymentTracker')
        }
      } catch (error) {
        console.error('Fetch payment error:', error)
        toast.error('Failed to load payment details', { autoClose: 2000 })
        navigate('/paymentTracker')
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchPaymentData()
  }, [id, navigate])

  // Fetch active suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const payload = {
          action: 'get-active-suppliers'
        }
        const response = await axios.post(API_URL, payload)

        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (parseError) {
            responseData = response.data
          }
        }

        if (responseData.success === true && responseData.data) {
          setSuppliers(responseData.data)
        } else {
          toast.error(responseData.message || 'Failed to fetch suppliers', {
            autoClose: 2000
          })
        }
      } catch (error) {
        console.error('Fetch suppliers error:', error)
        toast.error('Failed to load suppliers', { autoClose: 2000 })
      }
    }

    fetchSuppliers()
  }, [])

  // Get current user role
  useEffect(() => {
    const role = localStorage.getItem('role')
    if (role) {
      setCurrentUserRole(role.toLowerCase())
    }
  }, [])

  // Handle supplier selection
  const handleSupplierChange = (e) => {
    const selectedSupplierId = e.target.value
    const selectedSupplier = suppliers.find(s => s._id === selectedSupplierId)
    
    if (selectedSupplier) {
      setFormData(prev => ({
        ...prev,
        supplierName: selectedSupplier.clientname,
        supplierId: selectedSupplier._id
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        supplierName: '',
        supplierId: ''
      }))
    }
  }

  // Handle numeric fields (Amount only) - allow only numbers and one decimal
  const handleAmountChange = (e) => {
    const { name, value } = e.target
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  // Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      const next = { ...prev, [name]: value }
      if (name === 'paymentStatus') {
        if (value === 'Paid' && prev.paymentStatus !== 'Paid') {
          next.invoiceStatus = ''
        }
        if (value !== 'Paid') {
          next.invoiceStatus = ''
          next.comments = ''
        }
      }
      return next
    })
  }

  // Simple form validation
  const validateForm = () => {
    if (!formData.supplierName.trim()) {
      toast.error('Supplier name is required', { autoClose: 2000 })
      return false
    }
    if (!formData.beneficiaryAccountNumber.trim()) {
      toast.error('Beneficiary account number is required', { autoClose: 2000 })
      return false
    }
    if (!formData.beneficiaryIfscNumber.trim()) {
      toast.error('Beneficiary IFSC number is required', { autoClose: 2000 })
      return false
    }
    if (!formData.amount || isNaN(parseFloat(formData.amount))) {
      toast.error('Valid amount is required', { autoClose: 2000 })
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => {
        navigate('/')
      }, 2000)
      return
    }

    setIsLoading(true)

    try {
      const amount = parseFloat(formData.amount) || 0

      const payload = {
        action: 'update-payment',
        id: id,
        supplierName: formData.supplierName.trim(),
        supplierId: formData.supplierId.trim(),
        beneficiaryAccountNumber: formData.beneficiaryAccountNumber.trim(),
        beneficiaryIfscNumber: formData.beneficiaryIfscNumber.trim(),
        amount: amount,
        debit: formData.debit.trim(),
        credit: formData.credit.trim(),
        requestStatus: formData.requestStatus,
        paymentDate: formData.paymentDate || null,
        paymentStatus: formData.paymentStatus,
        invoiceStatus: formData.invoiceStatus || '',
        comments: formData.comments.trim(),
        updatedBy: currentUserId
      }

      const response = await axios.post(API_URL, payload)

      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      if (responseData.success === true) {
        toast.success(responseData.message || 'Payment updated successfully', {
          autoClose: 2000
        })
        setTimeout(() => {
          navigate('/paymentTracker')
        }, 2000)
      } else {
        toast.error(responseData.message || 'Failed to update payment', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Update payment error:', error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(errorData.message || 'An error occurred while updating payment', {
          autoClose: 2000
        })
      } else {
        toast.error('Network error. Please check your connection and try again.', {
          autoClose: 2000
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingData) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Payment</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/paymentTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Payment</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/paymentTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Supplier Name</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="supplierId"
                            className="form-control"
                            value={formData.supplierId}
                            onChange={handleSupplierChange}
                            required
                          >
                            <option value="">Select Supplier</option>
                            {suppliers.map((supplier) => (
                              <option key={supplier._id} value={supplier._id}>
                                {supplier.clientname}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Beneficiary Account Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="beneficiaryAccountNumber"
                            className="form-control"
                            placeholder="Account Number"
                            value={formData.beneficiaryAccountNumber}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Beneficiary IFSC Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="beneficiaryIfscNumber"
                            className="form-control"
                            placeholder="IFSC Code"
                            value={formData.beneficiaryIfscNumber}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Amount</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="amount"
                            className="form-control"
                            placeholder="0.00"
                            value={formData.amount}
                            onChange={handleAmountChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Debit</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="debit"
                            className="form-control"
                            placeholder="Debit (text)"
                            value={formData.debit}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Credit</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="credit"
                            className="form-control"
                            placeholder="Credit (text)"
                            value={formData.credit}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Request Status</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="requestStatus"
                            className="form-control"
                            value={formData.requestStatus}
                            onChange={handleChange}
                          >
                            <option value="Advance">ADVANCE</option>
                            <option value="Credit">CREDIT</option>
                            <option value="Hold">HOLD</option>
                            <option value="Cancelled">CANCELLED</option>
                          </select>
                        </div>
                      </div>

                      {(currentUserRole === 'admin' || currentUserRole === 'supervisor') && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">Payment Date</label>
                          <div className="col-12 col-md-8">
                            <input
                              type="date"
                              name="paymentDate"
                              className="form-control"
                              value={formData.paymentDate}
                              onChange={handleChange}
                              required={formData.paymentStatus === 'Paid'}
                            />
                          </div>
                        </div>
                      )}

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Payment Status</label>
                        <div className="col-12 col-md-8">
                          {currentUserRole === 'admin' ? (
                            <select
                              name="paymentStatus"
                              className="form-control"
                              value={formData.paymentStatus}
                              onChange={handleChange}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Paid">Paid</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              value={formData.paymentStatus || 'Pending'}
                              readOnly
                              style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                            />
                          )}
                        </div>
                      </div>

                      {formData.paymentStatus === 'Paid' && (
                        <>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Invoice Status</label>
                            <div className="col-12 col-md-8">
                              <select
                                name="invoiceStatus"
                                className="form-control"
                                value={formData.invoiceStatus}
                                onChange={handleChange}
                              >
                                <option value="" disabled>
                                  Select status
                                </option>
                                <option value="Pending">Pending</option>
                                <option value="Accounted">Accounted</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Comments</label>
                            <div className="col-12 col-md-8">
                              <textarea
                                name="comments"
                                className="form-control"
                                placeholder="Comments"
                                value={formData.comments}
                                onChange={handleChange}
                                rows={3}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Updating...' : 'Update'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentTrackerEdit